import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { confirm } from "@/shared/lib/confirm";
import { Loader } from "@/shared/ui/Loader";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { StepUpTotpDialog } from "@/modules/auth/presentation/ui/StepUpTotpDialog";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import {
  useApproveRefundMutation,
  useRejectRefundMutation,
  useRefundQueueQuery,
  useRefundQueueSummaryQuery,
} from "../api/queries";
import { RefundQueueFilter } from "../ui/RefundQueueFilter";
import { RefundQueueTable } from "../ui/RefundQueueTable";
import { RefundQueueSummaryCards } from "../ui/RefundQueueSummaryCards";

type RefundQueueSearch = {
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function RefundQueuePage() {
  const { user } = useAuthState();
  const nav = useNavigate({ from: "/app/wallet/refund-queue" });
  const [stepUp, setStepUp] = useState<{ open: boolean; refundId: string | null }>({
    open: false,
    refundId: null,
  });
  const twoFactorEnabled =
    (user as { twoFactorEnabled?: boolean } | null)?.twoFactorEnabled === true;
  const search = useSearch({
    from: "/app/wallet/refund-queue",
  }) as RefundQueueSearch;

  const params = {
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
    search: search.search,
    sortField: (search.sortField ?? "createdAt") as "createdAt",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useRefundQueueQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useRefundQueueSummaryQuery();
  const approveMutation = useApproveRefundMutation();
  const rejectMutation = useRejectRefundMutation();

  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const runApprove = async (id: string, totpCode?: string) => {
    try {
      const res = await approveMutation.mutateAsync({ id, totpCode });
      if ("ok" in res && res.ok) {
        toast.success("ອະນຸມັດແລ້ວ");
      } else {
        toast.error("ຜິດພາດ", {
          description: (res as { error?: string }).error ?? "ALREADY_PROCESSED",
        });
      }
    } catch {
      // fetcher already toasts
    }
  };

  const handleApprove = async (id: string) => {
    const ok = await confirm({
      title: "ອະນຸມັດຄືນເງິນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການອະນຸມັດການຄືນເງິນນີ້?",
      actionText: "ອະນຸມັດ",
      cancelText: "ຍົກເລີກ",
    });
    if (!ok) return;
    if (twoFactorEnabled) {
      setStepUp({ open: true, refundId: id });
      return;
    }
    await runApprove(id);
  };

  const handleReject = async (id: string) => {
    const ok = await confirm({
      title: "ປະຕິເສດຄືນເງິນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປະຕິເສດການຄືນເງິນນີ້?",
      actionText: "ປະຕິເສດ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      const res = await rejectMutation.mutateAsync(id);
      if ("ok" in res && res.ok) {
        toast.success("ປະຕິເສດແລ້ວ");
      } else {
        toast.error("ຜິດພາດ", {
          description: (res as { error?: string }).error ?? "ALREADY_PROCESSED",
        });
      }
    } catch {
      // fetcher already toasts
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຄິວອະນຸມັດຄືນເງິນ",
              to: "/app/wallet/refund-queue",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <RotateCcwIcon className="size-5" />
              ຄິວອະນຸມັດຄືນເງິນ
            </h2>
            <p className="text-muted-foreground">
              ລາຍການຄືນເງິນ (ຫຼັງ branch ຮັບອໍເດີ) ທີ່ລໍຖ້າອະນຸມັດ.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <RefundQueueSummaryCards
            totalPendingRequests={summaryData?.totalPendingRequests ?? 0}
            totalPendingAmount={summaryData?.totalPendingAmount ?? 0}
            avgResponseTimeMinutes={summaryData?.avgResponseTimeMinutes ?? 0}
            autoApproveToday={summaryData?.autoApproveToday ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <RefundQueueFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <RefundQueueTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onApprove={handleApprove}
              onReject={handleReject}
              isApproving={
                approveMutation.isPending
                  ? approveMutation.variables?.id ?? null
                  : null
              }
              isRejecting={
                rejectMutation.isPending ? rejectMutation.variables : null
              }
            />
          )}
        </div>
      </Main>
      <StepUpTotpDialog
        open={stepUp.open}
        loading={approveMutation.isPending}
        onClose={() => setStepUp({ open: false, refundId: null })}
        onConfirm={async (totpCode) => {
          if (!stepUp.refundId) return;
          await runApprove(stepUp.refundId, totpCode);
          setStepUp({ open: false, refundId: null });
        }}
      />
    </>
  );
}
